import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/cn.js';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-sans text-[14px] font-semibold tracking-snug',
    'transition-colors duration-200 ease-jq',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jq-action-accent/60 focus-visible:ring-offset-2 focus-visible:ring-offset-jq-bg-base',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-jq-action-primary text-jq-action-primary-fg hover:bg-jq-action-primary/92',
        accent: 'bg-jq-action-accent text-jq-action-accent-fg hover:bg-jq-action-accent/90',
        secondary:
          'bg-jq-action-secondary text-jq-action-secondary-fg hover:bg-jq-action-secondary/80',
        outline:
          'border border-jq-fg-brand/30 bg-transparent text-jq-fg-brand hover:bg-jq-bg-sunken',
        ghost: 'bg-transparent text-jq-fg-1 hover:bg-jq-action-secondary',
        destructive:
          'bg-jq-action-destructive text-jq-action-destructive-fg hover:bg-jq-action-destructive/90',
        link: 'bg-transparent text-jq-fg-brand underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 rounded-sm px-3 text-[13px]',
        md: 'h-11 rounded px-5',
        lg: 'h-12 rounded-md px-6 text-[15px]',
        // 44px minimum touch target — valet curbside contexts
        touch: 'h-11 rounded px-5',
        icon: 'h-10 w-10 rounded',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
